import _styled, { default as _styled2 } from "styled-components";
import type { ComponentType } from "react";
import type { SanityRouteDocument } from "@ouster/shared";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { EditorialCardsBlockView } from "~/features/blocks/inventory/editorial-cards";
import { WebinarCardView } from "~/features/card/webinar-card";
import { refHeadline, refHref, refMedia, refText } from "~/features/sanity/page-builder/sections/editorial-cards";
import { SanityPageBuilder, type SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import { useBodyBackground } from "~/features/ui/use-body-background";
import type { SanityWebinarCard, SanityWebinarsPageData } from "./types";
type PageBuilderComponent = ComponentType<{
  sections?: SanityPageBuilderRenderSection[];
}>;

// Single-entry map so the (unused) type filter has a stable label, matching the
// contract of the editorial-cards section renderer.
const WEBINAR_TYPE_LABELS: Record<string, string> = {
  editorialCardsWebinarCard: "Webinars"
};
type WebinarVariant = "upcoming" | "recorded";

// Derive the card CTA from listing state, like the pre-cutover page did.
// Upcoming webinars register; recorded ones are watched. The migrated
// `linkText` is unreliable (many past events still read "Register Now"), so a
// register-style label on a recording is corrected to "Watch Now" while any
// other authored CTA is preserved.
function webinarCtaText(webinar: SanityWebinarCard, variant: WebinarVariant) {
  if (variant === "upcoming") {
    return webinar.linkText || "Register Now";
  }
  if (!webinar.linkText || /register/i.test(webinar.linkText)) {
    return "Watch Now";
  }
  return webinar.linkText;
}
type Props = {
  route: Pick<SanityRouteDocument, "title">;
  /** The page's own page-builder sections (the "lidar performance" series), rendered below the grids. */
  sections: SanityPageBuilderRenderSection[];
  webinarsData: SanityWebinarsPageData;
  /** Swap the section renderer when Presentation has live page-builder updates. */
  PageBuilderComponent?: PageBuilderComponent;
};

/** One webinar listing block ("Upcoming" / "Recorded") rendered through the
 * shared editorial-cards slider so it matches every other card collection. */
function WebinarSection({
  headline,
  webinars,
  variant,
  hasTopDivider
}: {
  headline: string;
  webinars: SanityWebinarCard[];
  variant: WebinarVariant;
  hasTopDivider: boolean;
}) {
  if (!webinars.length) {
    return null;
  }
  return <EditorialCardsBlockView<SanityWebinarCard> cards={webinars} headline={headline} headlineStart hasTopDivider={hasTopDivider} getCardKey={(webinar, index) => webinar._id ?? index} getCardType={() => "editorialCardsWebinarCard"} typeLabels={WEBINAR_TYPE_LABELS} renderCard={(webinar, {
    featured,
    cardSize
  }) => <WebinarCardView featured={featured} size={cardSize} headline={refHeadline(webinar)} text={refText(webinar)} url={refHref(webinar)} linkText={webinarCtaText(webinar, variant)} media={refMedia(webinar, "496/280")} />} />;
}

/**
 * Renders the `/insights/webinars` overview: an Upcoming grid, a Recorded grid,
 * then the page's authored page-builder sections. The overview document owns no
 * hero, so its title is retained as the page's accessible heading while the
 * cards are derived from the `webinar` collection. Rendered by `SanityRouteView`
 * in place of the plain hero + sections flow.
 */
export function SanityWebinarsPageView({
  route,
  sections,
  webinarsData,
  PageBuilderComponent = SanityPageBuilder
}: Props) {
  useBodyBackground({
    mode: "light"
  });
  const {
    isAnnouncementBarVisible
  } = useNavLayout();
  const topSpacing = isAnnouncementBarVisible ? {
    "marginTop": "calc(var(--announcement-bar-height) + var(--header-height))"
  } : {
    "marginTop": "var(--header-height)"
  };
  const hasUpcoming = webinarsData.upcoming.length > 0;
  return <_StyledDiv $_css={topSpacing}>
      <_StyledH>{route.title || "Webinars"}</_StyledH>
      <WebinarSection headline="Upcoming Webinars" webinars={webinarsData.upcoming} variant="upcoming" hasTopDivider={false} />
      <WebinarSection headline="Recorded Webinars" webinars={webinarsData.recorded} variant="recorded" hasTopDivider={hasUpcoming} />
      <PageBuilderComponent sections={sections} />
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-kap1ou-0"
})`${p => p.$_css}`;
var _StyledH = /*#__PURE__*/_styled2("h1").withConfig({
  displayName: "webinars___StyledH",
  componentId: "sc-14mjf6d-0"
})({
  "position": "absolute",
  "width": "1px",
  "height": "1px",
  "padding": "0",
  "margin": "-1px",
  "overflow": "hidden",
  "clip": "rect(0, 0, 0, 0)",
  "whiteSpace": "nowrap",
  "borderWidth": "0"
});